import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "../../../context/CurrencyContext";
import { getPackagesFromDB } from "../../../services/pricingService";
import logo from '../../../assets/images/logo.png';
import "../../Doctor/Packages.css";

const AllPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedCurrency, formatPrice, convertPrice, isLoading: currencyLoading } = useCurrency();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const packagesData = await getPackagesFromDB();
      setPackages(packagesData);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Fallback to default packages if DB fails
      setPackages(getDefaultPackages());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPackages = () => [
    {
      name: "Free",
      price: 0,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "no",
      chatsupport: "no",
      featuredprofile: "no",
      profilewebsite: "no",
      brochures: "no",
      articles: "no",
      hms: "no",
      awards: "no",
      memberships: "no",
      socialposts: "no",
      socialvideos: "no",
      duration: 0,
      featureddays: 0,
      key: "free"
    },
    {
      name: "Basic",
      price: 1500,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "no",
      chatsupport: "no",
      featuredprofile: "no",
      profilewebsite: "no",
      brochures: "no",
      articles: "no",
      hms: "no",
      awards: "no",
      memberships: "no",
      socialposts: "no",
      socialvideos: "no",
      duration: 30,
      featureddays: 0,
      key: "basic"
    },
    {
      name: "Business",
      price: 2500,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "yes",
      chatsupport: "yes",
      featuredprofile: "yes",
      profilewebsite: "yes",
      brochures: "10",
      articles: "15",
      hms: "yes",
      awards: "10",
      memberships: "10",
      socialposts: "30",
      socialvideos: "10",
      duration: 30,
      featureddays: 100,
      key: "business"
    }
  ];

  const getDisplayPrice = async (pkg) => {
    if (pkg.baseCurrency === selectedCurrency) {
      return { amount: pkg.price, symbol: getCurrencySymbol(selectedCurrency) };
    }

    try {
      const convertedAmount = await convertPrice(pkg.price, pkg.baseCurrency, selectedCurrency);
      return { amount: convertedAmount, symbol: getCurrencySymbol(selectedCurrency) };
    } catch (error) {
      console.error('Price conversion failed:', error);
      return { amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) };
    }
  };

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹',
      'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
    };
    return currencyMap[currency] || '₹';
  };

  if (loading) {
    return (
      <div className="packages-container">
        <div className="packages-header">
          <h1>Loading Packages...</h1>
          <div className="loading-spinner">⏳</div>
        </div>
      </div>
    );
  }

  return (
    <div className="packages-container">
      <div className="packages-header">
        <h1>All Packages</h1>
        <p>Choose the perfect plan for your healthcare practice</p>
        <div className="currency-note">
          Prices shown in {selectedCurrency}
          {currencyLoading && <span style={{ marginLeft: '0.5rem' }}>🔄</span>}
        </div>
      </div>

      <div className="packages-grid">
        {packages.map((pkg, idx) => (
          <PackageCard
            key={pkg.key}
            pkg={pkg}
            idx={idx}
            selectedCurrency={selectedCurrency}
            getDisplayPrice={getDisplayPrice}
            getCurrencySymbol={getCurrencySymbol}
          />
        ))}
      </div>

      {/* Why Choose MeddyList Section */}
      <div className="why-choose-section">
        <div className="why-choose-container d-flex justify-content-center align-items-center gap-3">
          {/* Main Title Circle */}
          <div className="why-choose-section-title" style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '30px'}}>
            <div className="main-title-circle">
              <div className="title-content">
                <h3>WHY CHOOSE</h3>
                <h3>MEDDYLIST</h3>
              </div>
            </div>

            {/* Central Hub Circle */}
            <div className="central-hub">
              <div className="hub-content">
                <div className="logo-section">
                  <img src={logo} alt="MeddyList Logo" className="meddylist-logo" />
                </div>
                <p>Join 10,000+ healthcare professionals who trust our platform to grow their practice and reach more patients.</p>
                <p>Experience proven results with 300% increase in patient inquiries and 150% growth in online visibility.</p>
              </div>
            </div>
          </div>


          {/* Feature Points */}
          <div className="feature-points">
            <div className="feature-point top">
              <div className="feature-circle purple">
                <i className="fas fa-gem"></i>
              </div>
              <div className="feature-text">
                <h4>Premium Quality</h4>
                <p>Enterprise-grade platform with cutting-edge features designed specifically for healthcare professionals.</p>
              </div>
            </div>

            <div className="feature-point middle-right">
              <div className="feature-circle blue">
                <i className="fas fa-clock"></i>
              </div>
              <div className="feature-text">
                <h4>24/7 Support</h4>
                <p>Round-the-clock customer support to ensure your practice runs smoothly at all times.</p>
              </div>
            </div>

            <div className="feature-point lower-right">
              <div className="feature-circle teal">
                <i className="fas fa-hand-pointer"></i>
              </div>
              <div className="feature-text">
                <h4>Easy Integration</h4>
                <p>Seamless integration with your existing systems and workflows for maximum efficiency.</p>
              </div>
            </div>

            <div className="feature-point bottom">
              <div className="feature-circle grey">
                <i className="fas fa-heart"></i>
              </div>
              <div className="feature-text">
                <h4>Patient-Centric</h4>
                <p>Built with patients in mind, ensuring better engagement and improved healthcare outcomes.</p>
              </div>
            </div>
          </div>

          {/* Connecting Lines */}
          <div className="connecting-lines">
            <div className="line line-1"></div>
            <div className="line line-2"></div>
            <div className="line line-3"></div>
            <div className="line line-4"></div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <Link to="/" className="back-to-home-btn">
          <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
          Back to Home
        </Link>
      </div>
    </div>
  );
};


// Separate component for package card with dynamic pricing
const PackageCard = ({ pkg, idx, selectedCurrency, getDisplayPrice, getCurrencySymbol }) => {
  const [displayPrice, setDisplayPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    loadPrice();
  }, [pkg, selectedCurrency]);

  const loadPrice = async () => {
    setPriceLoading(true);
    try {
      const price = await getDisplayPrice(pkg);
      setDisplayPrice(price);
    } catch (error) {
      console.error('Error loading price:', error);
      setDisplayPrice({ amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) });
    } finally {
      setPriceLoading(false);
    }
  };

  if (!displayPrice) {
    return (
      <div className="package-card">
        <div className="package-header">
          <h3>{pkg.name}</h3>
          <div className="package-price">
            <span className="loading-price">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`package-card ${idx === 1 ? 'is-featured' : ''}`}>
      {idx === 1 && <div className="package-badge">POPULAR</div>}
      <div className="package-header">
        <h3>{pkg.name}</h3>
        <div className="package-price">
          <span className="currency">{displayPrice.symbol}</span>
          <span className="amount">
            {priceLoading ? '...' : displayPrice.amount.toFixed(2)}
          </span>
          <span className="period">/{pkg.period}</span>
        </div>
        {selectedCurrency !== pkg.baseCurrency && (
          <div className="original-price" style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
            Original: {getCurrencySymbol(pkg.baseCurrency)}{pkg.price}
          </div>
        )}
      </div>

      <div className="package-features">
        <ul>
          {pkg.autopost && <li><span className="feature-label">Auto Post</span> <span className="feature-value">{pkg.autopost == "no" ? '✗' : '✓'}</span></li>}
          {pkg.chatsupport && <li><span className="feature-label">Chat Support</span> <span className="feature-value">{pkg.chatsupport == "no" ? '✗' : '✓'}</span></li>}
          {pkg.featuredprofile && <li><span className="feature-label">Featured Profile</span> <span className="feature-value">{pkg.featuredprofile == "no" ? '✗' : '✓'}</span></li>}
          {pkg.profilewebsite && <li><span className="feature-label">Profile Website</span> <span className="feature-value">{pkg.profilewebsite == "no" ? '✗' : '✓'}</span></li>}
          {pkg.brochures && <li><span className="feature-label">Brochures</span> <span className="feature-value">{pkg.brochures == "no" ? '✗' : pkg.brochures}</span></li>}
          {pkg.articles && <li><span className="feature-label">Articles</span> <span className="feature-value">{pkg.articles == "no" ? '✗' : pkg.articles}</span></li>}
          <li><span className="feature-label">Listing</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Digital Visiting Card</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Bookings</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Quick Chat</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">HMS Integration</span> <span className="feature-value">{pkg.hms == "no" ? '✗' : '✓'}</span></li>
          {pkg.memberships && <li><span className="feature-label">Memberships</span> <span className="feature-value">{pkg.memberships == "no" ? '✗' : pkg.memberships}</span></li>}
          {pkg.socialposts && <li><span className="feature-label">Social Posts</span> <span className="feature-value">{pkg.socialposts}</span></li>}
          {pkg.socialvideos && <li><span className="feature-label">Social Videos</span> <span className="feature-value">{pkg.socialvideos}</span></li>}
          <li><span className="feature-label">Featured Days</span> <span className="feature-value">{pkg.featureddays}</span></li>
        </ul>
      </div>

      <Link to={`/package/${pkg.key}`} className="package-button" data-key={pkg.key}>
        {pkg.name === "Free" ? "Get Started" : "Choose Plan"}
      </Link>
    </div>
  );
};

export default AllPackages;
